import {
  useTheme,
  useBoxStyle,
  ListItem,
  AHSeparator,
  Box,
  SectionHeader,
  AHTodoCategoryIcon,
  AHIcon,
} from "arkh";
import React, { useEffect, useMemo } from "react";
import { ScrollView, Switch, Platform, View } from "react-native";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { TeamAdminLayoutQuery } from "./__generated__/TeamAdminLayoutQuery.graphql";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { TeamAdminLayoutSetTeamModeMutation } from "./__generated__/TeamAdminLayoutSetTeamModeMutation.graphql";
import { TeamAdminLayoutFeature_TeamFeature$key } from "./__generated__/TeamAdminLayoutFeature_TeamFeature.graphql";
import { TeamAdminLayout_Team$key } from "./__generated__/TeamAdminLayout_Team.graphql";
import { TouchableOpacity } from "react-native-gesture-handler";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { ReorderableList } from "../../components/mobile/ReoardrableList/ReordrableList";
import { useNavigationHeader } from "../../common/navigationHeaderOptions";
import { useEvent } from "../../common/useEvent";
import { TeamAdminScreenProp } from "./TeamAdmin";
import { FormattedMessage } from "react-intl";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const LIST_ITEM_HEIGHT = 50;
export function TeamAdminLayoutScreen(
  props: TeamAdminScreenProp<"TeamAdminLayout">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <TeamAdminLayout
          key={fk}
          teamId={props.route.params.teamId}
          onGoBack={props.navigation.goBack}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}
function TeamAdminLayout(props: {
  teamId: string;

  onGoBack: () => void;
}) {
  const data = useLazyLoadQuery<TeamAdminLayoutQuery>(
    graphql`
      query TeamAdminLayoutQuery($teamId: ID!) {
        store {
          team(id: $teamId) {
            ...TeamAdminLayout_Team
            availableFeatures {
              id
              isHidden
              pinnedOrder
            }
          }
        }
      }
    `,
    {
      teamId: props.teamId,
    },
    {
      fetchPolicy: "store-and-network",
    }
  );

  if (!data.store.team) {
    return null;
  }
  return (
    <TeamAdminLayoutComponent
      team={data.store.team}
      onGoBack={props.onGoBack}
    />
  );
}
function TeamAdminLayoutComponent(props: {
  team: TeamAdminLayout_Team$key;
  onGoBack: () => void;
}) {
  const team = useFragment(
    graphql`
      fragment TeamAdminLayout_Team on Team {
        id
        color
        type
        availableFeatures {
          __typename
          id
          isHidden
          pinnedOrder
          ...TeamAdminLayoutFeature_TeamFeature
        }
      }
    `,
    props.team
  );

  const remotePinned = team.availableFeatures
    .filter((feature) => feature.pinnedOrder !== null && !feature.isHidden)
    .map((feature) => feature.id);
  const [pinned, setPinned] = React.useState(remotePinned);

  const remoteHidden = team.availableFeatures
    .filter((feature) => feature.isHidden)
    .map((feature) => feature.id);
  const [hidden, setHidden] = React.useState(remoteHidden);

  const remoteType = team.type;
  const [teamType, setTeamType] = React.useState(remoteType);

  const isDirty = useMemo(() => {
    if (remoteType !== teamType) return true;
    if (remoteHidden.join() !== hidden.join()) return true;
    if (remotePinned.join() !== pinned.join()) return true;

    return false;
  }, [remoteType, teamType, remoteHidden, hidden, remotePinned, pinned]);

  const reset = useEvent(() => {
    setPinned(remotePinned);
    setHidden(remoteHidden);
    setTeamType(remoteType);
  });
  useEffect(() => {
    if (!isDirty) {
      reset();
    }
  }, [isDirty, reset]);
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
    color: "surface-background",
    insets: { bottom: insets.bottom },
  });

  const [save, saveInFlight] = useMutation<TeamAdminLayoutSetTeamModeMutation>(
    graphql`
      mutation TeamAdminLayoutSetTeamModeMutation(
        $teamId: ID!
        $teamType: TeamType!
        $hiddenIds: [ID!]!
        $pinnedIds: [ID!]!
      ) {
        team(id: $teamId) {
          updateFeatures(newHiddenIds: $hiddenIds, newPinnedIds: $pinnedIds) {
            features {
              id
            }
            availableFeatures {
              __typename
              id
              isHidden
              pinnedOrder
              ...TeamAdminLayoutFeature_TeamFeature
            }
          }
          updateType(newType: $teamType) {
            type
            id
          }
        }
      }
    `
  );

  useNavigationHeader({
    type: "SAVE",
    saveLoading: saveInFlight,
    disabled: !isDirty,
    title: "",
    onCancel() {
      props.onGoBack();
    },
    onSave() {
      save({
        variables: {
          teamId: team.id,
          teamType,
          hiddenIds: hidden,
          pinnedIds: pinned,
        },
        onCompleted() {
          props.onGoBack();
        },
      });
    },
  });

  const pinnedItems = pinned
    .map((id) => team.availableFeatures.find((f) => f.id === id)!)
    .filter((f) => f);

  const nonPinned = team.availableFeatures.filter(
    (f) => !pinned.includes(f.id)
  );

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme["surface-background"] }}
      contentContainerStyle={contentContainerStyle}
    >
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Menu Priority"
            description="TeamAdmin menu priority section header"
          />
        </SectionHeader.Title>
        <SectionHeader.Description>
          <FormattedMessage
            defaultMessage="Sort or hide functions on the team screen to better maintain focus on the right things."
            description="TeamAdmin menu priority section description"
          />
        </SectionHeader.Description>
      </SectionHeader>

      <ReorderableList
        items={pinnedItems}
        keyExtractor={(item) => item.id}
        onReorder={(order) => {
          setPinned(Object.keys(order).sort((a, b) => order[a] - order[b]));
        }}
        renderItem={(feature, index, arr) => {
          return (
            <FeatureListItem
              color={team.color}
              feature={feature}
              isFirst={index === 0}
              isLast={index === arr.length - 1}
              onTogglePin={() => {
                setPinned((prev) => prev.filter((id) => id !== feature.id));
              }}
              mode="PINNED"
              onToggleHidden={() => {}}
            />
          );
        }}
        itemHeight={LIST_ITEM_HEIGHT}
      />

      <Box spaceVertical="small" />

      {nonPinned.map((feature, index, arr) => {
        const isHidden = hidden.includes(feature.id);
        return (
          <React.Fragment key={feature.id}>
            <FeatureListItem
              color={team.color}
              feature={feature}
              isFirst={index === 0}
              isLast={index === arr.length - 1}
              onTogglePin={() => {
                setPinned((prev) => [...prev, feature.id]);
              }}
              mode={isHidden ? "HIDDEN" : "NORMAL"}
              onToggleHidden={() => {
                setHidden((prev) =>
                  isHidden
                    ? prev.filter((p) => p !== feature.id)
                    : [...prev, feature.id]
                );
              }}
            />
            {index === arr.length - 1 ? null : <AHSeparator />}
          </React.Fragment>
        );
      })}

      <Box space="medium" />
      <SectionHeader>
        <SectionHeader.Title>
          <FormattedMessage
            defaultMessage="Team Type"
            description="TeamAdmin team type section header"
          />
        </SectionHeader.Title>
        <SectionHeader.Description>
          <FormattedMessage
            defaultMessage="Enable checkout mode for quick switching between team, files and PLU."
            description="TeamAdmin team type section description"
          />
        </SectionHeader.Description>
      </SectionHeader>
      <ListItem
        isFree
        right={
          <Switch
            onValueChange={(event) => {
              setTeamType(event ? "CHECKOUT" : "FLOOR");
            }}
            value={teamType === "CHECKOUT"}
            trackColor={{
              false: theme["surface-backgroundControl"],
              true: theme.primary,
            }}
            {...(Platform.OS === "web"
              ? { activeThumbColor: theme["surface-primary"] }
              : {})}
            thumbColor={theme["surface-primary"]}
          />
        }
      >
        <ListItem.Title>
          <FormattedMessage
            defaultMessage="Checkout Mode"
            description="TeamAdmin checkout mode toggle label"
          />
        </ListItem.Title>
      </ListItem>
    </ScrollView>
  );
}

function FeatureListItem(props: {
  feature: TeamAdminLayoutFeature_TeamFeature$key;
  isFirst?: boolean;
  isLast?: boolean;
  isPinned?: boolean;
  onTogglePin: () => void;
  onToggleHidden: () => void;
  mode: "HIDDEN" | "PINNED" | "NORMAL";
  color: string | null;
}) {
  const intl_tokens = useIntlTokens();
  const data = useFragment(
    graphql`
      fragment TeamAdminLayoutFeature_TeamFeature on TeamFeature {
        id
        __typename

        ... on TodoCategoryTeamFeature {
          todoCategoryTeam {
            category {
              icon
              title
            }
          }
        }
      }
    `,
    props.feature
  );

  const renderTitle = () => {
    switch (data.__typename) {
      case "SignalTeamFeature":
        return intl_tokens.Signals;
      case "TodoCategoryTeamFeature":
        return data.todoCategoryTeam?.category.title;
      case "FileTeamFeature":
        return intl_tokens.Files;
      case "KPITeamFeature":
        return intl_tokens.KPIs;
      case "ProductTeamFeature":
        return intl_tokens.Products;
      case "ProductDateTrackerTeamFeature":
        return intl_tokens.ExpireDates;
      case "EmployeeScheduleTeamFeature":
        return intl_tokens.EmployeeSchedule;
      case "CalendarTeamFeature":
        return intl_tokens.Calendar;
      default:
        return "";
    }
  };
  const renderIcon = () => {
    switch (data.__typename) {
      case "EmployeeScheduleTeamFeature":
        return <AHIcon type="schedule" />;
      case "ProductTeamFeature":
        return <AHIcon type="product" />;
      case "CalendarTeamFeature":
        return <AHIcon type="calendar" />;
      case "SignalTeamFeature":
        return <AHIcon type="signal" />;
      case "TodoCategoryTeamFeature":
        return (
          <AHTodoCategoryIcon
            icon={data.todoCategoryTeam?.category.icon ?? "Check"}
          />
        );
      case "FileTeamFeature":
        return <AHIcon type="files" />;
      case "KPITeamFeature":
        return <AHIcon type="KPI" />;
      case "ProductDateTrackerTeamFeature":
        return <AHIcon type="tag" />;
      default:
        return null;
    }
  };

  const renderRight = () => {
    switch (props.mode) {
      case "PINNED":
        return (
          <TouchableOpacity
            style={{ flexDirection: "row" }}
            onPress={props.onTogglePin}
            hitSlop={{ left: 100, top: 15, bottom: 15 }}
          >
            <AHIcon
              type="pin"
              customColor={props.color ?? undefined}
              color="darkText"
              spaceRight="large"
            />
            <Box spaceRight="large" />
          </TouchableOpacity>
        );
      case "HIDDEN":
        return (
          <TouchableOpacity
            style={{ flexDirection: "row" }}
            hitSlop={{ right: 100, top: 15, bottom: 15, left: 100 }}
            onPress={props.onToggleHidden}
          >
            <AHIcon type="hide" color="washedText" spaceRight="medium" />
          </TouchableOpacity>
        );

      case "NORMAL":
        return (
          <Box style={{ flexDirection: "row" }}>
            <TouchableOpacity
              hitSlop={{ left: 100, top: 15, bottom: 15 }}
              onPress={props.onTogglePin}
            >
              <AHIcon type="pin-off" color="darkText" />
            </TouchableOpacity>
            <TouchableOpacity
              hitSlop={{ right: 100, top: 15, bottom: 15 }}
              onPress={props.onToggleHidden}
            >
              <AHIcon
                spaceLeft="large"
                type="show"
                color="darkText"
                spaceRight="medium"
              />
            </TouchableOpacity>
          </Box>
        );
    }
  };
  return (
    <View style={{ opacity: props.mode === "HIDDEN" ? 0.4 : 1 }}>
      <ListItem
        isReorderable={props.mode === "PINNED"}
        disabled
        left={renderIcon()}
        forceHeight={LIST_ITEM_HEIGHT - 1}
        isFirst={props.isFirst}
        isLast={props.isLast}
        right={renderRight()}
      >
        <ListItem.Title>{renderTitle()}</ListItem.Title>
      </ListItem>
    </View>
  );
}
